import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  IconButton,
  InputBase,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import ListPagination from "../../containers/ListPagination";
import { useRuleStyles } from "../Rules/styles";
import { sortDirection } from "../Rules/common";

const useStyles = makeStyles({
  // Fixed layout: the columns share the width of the screen and long text wraps, so the
  // table never scrolls sideways (the spec's 1366 px rule).
  table: { tableLayout: "fixed", width: "100%" },
  wrap: { whiteSpace: "normal", wordBreak: "break-word" },
  lines: { display: "flex", flexDirection: "column" },
  searchCell: {
    padding: "4px 8px 8px",
    backgroundColor: "#f2f5fa",
    verticalAlign: "top",
  },
  search: {
    width: "100%",
    fontSize: "13px",
    backgroundColor: "#fff",
    border: "1px solid #c4c9d4",
    borderRadius: "4px",
    padding: "0 6px",
    minHeight: "32px",
    "&.Mui-focused": { borderColor: "#1f5fbf" },
    "& input": { padding: "4px 0" },
  },
  actionsCell: { padding: "4px", whiteSpace: "nowrap" },
});

const blank = (value) => value == null || String(value).trim() === "";

/** Only a web URL may become a link: a `javascript:` value typed into the field stays text. */
export const isHttpUrl = (value) =>
  /^https?:\/\/\S+$/i.test(String(value || ""));

/** The Venue cell: pool, school, aquatic center and building, blanks skipped. */
export const venueLines = (row) =>
  [row.poolName, row.schoolInstitution, row.aquaticCenter, row.building]
    .map((v) => (blank(v) ? "" : String(v).trim()))
    .filter(Boolean);

/** The Street cell: lines 1 to 3, blanks skipped. */
export const streetLines = (row) =>
  [row.streetOne, row.streetTwo, row.streetThree]
    .map((v) => (blank(v) ? "" : String(v).trim()))
    .filter(Boolean);

/** What the action icons call the row: its first venue line, else "venue <id>". */
export const venueLabel = (row) => venueLines(row)[0] || `venue ${row.id}`;

const Lines = ({ lines, className }) => (
  <span className={className}>
    {lines.map((line, index) => (
      <span key={`${index}-${line}`}>{line}</span>
    ))}
  </span>
);

Lines.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.string).isRequired,
  className: PropTypes.string,
};

/**
 * `sort` is the backend sort token (admin/venues `sort`); columns without one don't sort.
 * `search` is the list-state key the box under the header edits; columns without one have no
 * box. `width` is a share of the table width (they add up to 100 with the Actions column).
 */
export const COLUMNS = [
  {
    key: "id",
    label: "ID",
    sort: "Id",
    search: "id",
    width: "4.5%",
    numeric: true,
  },
  { key: "venue", label: "Venue", sort: "Name", search: "name", width: "16%" },
  {
    key: "street",
    label: "Street",
    sort: "Street",
    search: "street",
    width: "13.5%",
  },
  { key: "city", label: "City", sort: "City", search: "city", width: "9%" },
  {
    key: "county",
    label: "County",
    sort: "County",
    search: "county",
    width: "8.5%",
  },
  { key: "state", label: "State", sort: "State", width: "5%" },
  {
    key: "postalCode",
    label: "Postal code",
    sort: "PostalCode",
    search: "postalCode",
    width: "7%",
  },
  { key: "country", label: "Country", sort: "Country", width: "6%" },
  {
    key: "timezone",
    label: "Timezone",
    sort: "Timezone",
    search: "timezone",
    width: "11%",
  },
  { key: "map", label: "Map", width: "5.5%" },
  { key: "website", label: "Website", width: "6%" },
];

const ACTIONS_WIDTH = "8%";

const VenuesTable = ({
  rows,
  state,
  drafts,
  total,
  loading,
  onSort,
  onSearch,
  onPage,
  onEdit,
  onDelete,
}) => {
  const classes = useRuleStyles();
  const own = useStyles();
  const columnCount = COLUMNS.length + 1;

  const missing = (
    <Chip size="small" label="Missing" className={classes.chipWarn} />
  );

  /** "Map" / "Website": a link for http(s) values; other text is shown and flagged. */
  const linkCell = (value, text, whenBlank) => {
    if (blank(value)) return whenBlank;
    if (isHttpUrl(value)) {
      return (
        <Link href={value} target="_blank" rel="noopener noreferrer">
          {text}
        </Link>
      );
    }
    return (
      <span className={own.lines}>
        <span>{value}</span>
        <span>
          <Chip size="small" label="Not a link" className={classes.chipWarn} />
        </span>
      </span>
    );
  };

  const cell = (col, row) => {
    switch (col.key) {
      case "id":
        return row.id;
      case "venue":
        return <Lines lines={venueLines(row)} className={own.lines} />;
      case "street":
        return <Lines lines={streetLines(row)} className={own.lines} />;
      case "country":
        return blank(row.countryIso) ? (
          ""
        ) : (
          <span title={row.countryName || undefined}>{row.countryIso}</span>
        );
      case "timezone":
        return blank(row.timezone) ? missing : row.timezone;
      case "map":
        return linkCell(row.googleMapsLink, "Map", missing);
      case "website":
        return linkCell(row.website, "Website", "");
      default:
        return row[col.key] ?? "";
    }
  };

  return (
    <TableContainer component={Paper}>
      {/* Rows stay on screen while the next page loads. */}
      <Box height={4}>
        {loading && <LinearProgress aria-label="Loading venues" />}
      </Box>
      <Table
        size="small"
        className={own.table}
        aria-label="Venues"
        aria-busy={loading}
      >
        <colgroup>
          {COLUMNS.map((col) => (
            <col key={col.key} style={{ width: col.width }} />
          ))}
          <col style={{ width: ACTIONS_WIDTH }} />
        </colgroup>
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                scope="col"
                className={`${classes.headerCell}${
                  col.numeric ? ` ${classes.number}` : ""
                }`}
                sortDirection={
                  col.sort ? sortDirection(state, col.sort) : false
                }
              >
                {col.sort ? (
                  <TableSortLabel
                    active={state.sort === col.sort}
                    direction={sortDirection(state, col.sort) || "asc"}
                    onClick={() => onSort(col.sort)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
            <TableCell scope="col" className={classes.headerCell}>
              <span className="sr-only">Actions</span>
            </TableCell>
          </TableRow>
          {/* One search box under each searchable column. */}
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell key={col.key} className={own.searchCell}>
                {col.search ? (
                  <InputBase
                    className={own.search}
                    inputProps={{
                      "aria-label": `Search ${col.label}`,
                      inputMode: col.numeric ? "numeric" : undefined,
                    }}
                    value={drafts[col.search] || ""}
                    onChange={(e) => onSearch(col.search, e.target.value)}
                  />
                ) : null}
              </TableCell>
            ))}
            <TableCell className={own.searchCell} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} className={classes.empty}>
                No venues match these filters.
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  className={`${classes.cell} ${own.wrap}${
                    col.numeric ? ` ${classes.number}` : ""
                  }`}
                >
                  {cell(col, row)}
                </TableCell>
              ))}
              <TableCell className={`${classes.cell} ${own.actionsCell}`}>
                <Box className={classes.actions}>
                  <IconButton
                    className={classes.iconButton}
                    aria-label={`Edit ${venueLabel(row)}`}
                    title="Edit"
                    onClick={() => onEdit(row)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    className={classes.iconButton}
                    aria-label={`Delete ${venueLabel(row)}`}
                    title="Delete"
                    onClick={() => onDelete(row)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ListPagination
        filters={{ page: state.page, limit: state.size, total }}
        fetchFunction={onPage}
      />
    </TableContainer>
  );
};

VenuesTable.propTypes = {
  rows: PropTypes.array.isRequired,
  state: PropTypes.object.isRequired,
  drafts: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onSort: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onPage: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default VenuesTable;
